import {MutableRefObject, useEffect, useMemo, useRef, useState} from 'react';

import {Box, Divider, Typography} from '@mui/material';
import * as sentry from '@sentry/react';
import {useTranslation} from 'react-i18next';
import {When} from 'react-if';

import {config} from 'config/env';
import useAnalytics, {ANALYTICS, SUGGESTEDLIST_CHANNELS, EVENT_SOURCE} from 'contexts/analytics-context';
import {useAppDispatch, useAppSelector} from 'hooks';
import {useAppMode} from 'hooks/useAppMode';
import {useExpressWeightLimit} from 'hooks/useExpressWeightLimit';
import {updateBasketStoreCode} from 'services/basket/basket-service';
import {createPartialCheckout, createPartialCheckoutV3, getCheckoutIdV2} from 'services/checkout/checkout-service';
import MultiStoreService from 'services/multistore/multistore-service';
import ReactNative from 'services/react-native';
import SendBrowserEvent from 'services/send-browser-event';
import {updateBasketAddressAction} from 'store/actions/addresses-action';
import {setCurrentStoreAction} from 'store/actions/app-action';
import {fetchUserDetailsAction} from 'store/actions/auth-action';
import {
  batchUpdateItemsQuantityAction,
  deleteSelectedItemsAction,
  getBasketCalculationAction,
  getBasketRecommendationsAction,
  getRM3PromotionsAction,
  removeUnavailableItemsAction,
  setBasketQuantityChangedProductMapAction,
  setSelectedItemsAction,
  setVariantIdsOfItemThatWasAdjustedQuantityAction,
  updateMetaDataAction
} from 'store/actions/basket-action';
import {
  BasketErrorTypes,
  BasketLineItem,
  UserAddress,
  BasketState,
  IBasketQuantityChangedProductMap,
  ItemToUpdateQuantity,
  ExpressTimeFrame,
  BasketItemInput,
  TPinMapSelectedLocation
} from 'types';
import {ProductUnavailableReasonCodes} from 'types/recommendations';
import {
  createCheckoutPayload,
  DeviceType,
  filterUnavailableProducts,
  getAlcoholProducts,
  getAlcoholVolumeInLiters,
  getDeviceType,
  getIdsFromBasketItems,
  getProductAvailability,
  hasItemsInBasket,
  is1PMakroStore,
  isDropShip,
  isFeatureEnabled,
  isTimeEligibleForExpress,
  mapUserAddressToShippingAddress
} from 'utils';
import {
  filter1PItems,
  filter3PItems,
  isBasketRecommendationEnabled,
  isSyncAddressEnabled,
  isSyncBasketEnabled
} from 'utils/helper';
import {checkIfAddressLocationIsDeliverable} from 'utils/location';
import {AddressDrawer} from 'views/address-drawer/AddressDrawerContainer';
import AddressFormView from 'views/address-form/AddressFormView';
import {PinMapModal} from 'views/address-form/PinMapModal';
import Body from 'views/basket-body/Body';
import {Footer} from 'views/basket-footer/BasketFooterContainer';
import Header from 'views/basket-header/Header';
import BasketPopUpDialog from 'views/basket-modals/BasketPopUpDialog';
import BasketSummaryModal from 'views/basket-modals/BasketSummaryModal';
import FlexiBasketConfirmModal from 'views/basket-modals/FlexiBasketConfirmModal';
import ProductItem from 'views/basket-modals/ProductItem';
import {BangkokCoordinates} from 'views/map-wrapper/MapWrapper';

import {StyledBasket, StyledProductInfoWrapper, StyledSectionContentContainer, StyledSectionWrap} from './styles';
import {ALCOHOL_VOLUME_THRESHOLD} from '../../constants/index';

interface IBasektAffectedModalData {
  modalShown: boolean;
  quantityUpdatedItems: number;
  notReadyToPurchase: number;
}

const BasketWrapper = () => {
  const {t, i18n} = useTranslation();
  const dispatch = useAppDispatch();
  const {isExpress} = useAppMode();
  const {isWithinThreshold, threshold} = useExpressWeightLimit();
  const analytics = useAnalytics();

  const [openFullMapView, setOpenFullMapView] = useState<boolean>(false);
  const [isAddressFormModalOpen, setIsAddressFormModalOpen] = useState<boolean>(false);
  const [isPinMapViewOpen, setIsPinMapViewOpen] = useState<boolean>(false);
  const [addressBeingEdited, setAddressBeingEdited] = useState<UserAddress>();
  const [basketAffectedModalData, setBasketAffectedModalData] = useState<IBasektAffectedModalData>({
    modalShown: false,
    quantityUpdatedItems: 0,
    notReadyToPurchase: 0
  });
  const [isAddressDrawerOpen, setAddressDrawerOpen] = useState(false);
  const [checkoutInitiated, setCheckoutInitiated] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showBasketSummaryModal, setShowBasketSummaryModal] = useState(false);

  const [pinMapData, setPinMapData] = useState<TPinMapSelectedLocation | null>(null);

  const isPinMapV2Enabled = config.IS_PIN_MAP_V2_ENABLED;

  const isAddressFlowRevampEnabled = true;
  const isThai = i18n.language === 'th';
  const isReactNativeWebView = ReactNative.isReactNativeWebView();

  const {basket, isLoading, isBasketApiFailed, basketQuantityChangedProductMap, selectedItems} = useAppSelector(
    (state) => state?.basket
  );

  const has1PSelected = useMemo<boolean>(() => {
    return !!basket?.basketItems
      ?.filter((item) => selectedItems?.includes(item.productId))
      .some((item) => !isDropShip(item.productDetails?.sellerDetails.marketplaceType));
  }, [basket?.basketItems, selectedItems]);

  const {
    currentStore,
    isDirectCheckout,
    defaultMembershipCard,
    pricezaClickId,
    accessTradeClickId,
    isAppLoading,
    appVersion,
    deviceOS,
    locations
  } = useAppSelector((state) => state?.app);
  const {idToken, customerId} = useAppSelector((state) => state?.auth) || {};

  const userAddressList = useAppSelector((state) => state?.addresses?.availableAddresses) || [];
  const shippingAddress = useAppSelector((state) => state?.addresses?.selectedShippingAddress);
  const timer: MutableRefObject<null | ReturnType<typeof setTimeout>> = useRef(null);
  const unavailableProducts = basket?.unavailableItems || [];
  const zipcodeForUpdateBasket = currentStore?.zipcode;
  const currentStoreCodes = currentStore?.storeCodes;
  const isSyncBasket = isSyncBasketEnabled();
  const storeSelecteditems = useAppSelector((state) => state?.basket?.selectedItems);
  const localeSelectedItems = JSON.parse(localStorage.getItem('selectedItems') || '[]') || [];
  const persistedSelectedItems: string[] = isSyncBasket ? storeSelecteditems : localeSelectedItems;
  const numberOfItems = persistedSelectedItems?.length || 0;

  const toggleAddressDrawer = () => setAddressDrawerOpen(!isAddressDrawerOpen);

  const isFlexibleBasketEnabled = config.IS_FLEXIBLE_BASKET_ENABLED;

  const mininumVersion = config.IS_COMPLEX_PROMOTION_ENABLED;

  const isComplexPromotionEnabled = isFeatureEnabled(mininumVersion, appVersion || '1.0.0');

  const isSyncAddress = isSyncAddressEnabled();
  const isCheckoutV3 =
    (!isReactNativeWebView && config.IS_WEB_CHECKOUT_V3_ENABLE) ||
    (isReactNativeWebView && config.IS_APP_CHECKOUT_V3_ENABLE);

  useEffect(() => {
    if (isComplexPromotionEnabled && basket && getDeviceType() !== DeviceType.DESKTOP) {
      const skuIds = basket?.basketItems?.map((p) => p.productDetails.sku) ?? [];

      if (skuIds.length) {
        dispatch(getRM3PromotionsAction(skuIds));
      }
    }
  }, [basket, dispatch, isComplexPromotionEnabled]);

  const onShipToClickHandle = () => {
    const needToOpenAddressForm = !userAddressList?.length;
    if (idToken && needToOpenAddressForm) {
      isPinMapV2Enabled ? _showPinMapModal() : setIsAddressFormModalOpen(true);
    } else {
      toggleAddressDrawer();
    }
  };
  const hasProductInBasket = hasItemsInBasket(basket);
  const isDisabledCheckoutBtn = () => {
    const basketTotal = basket?.prices?.totalDisplayPricePostVoucher || 0;
    const noCurrentStore = !currentStore?.storeCodes?.length;
    // const isSelectedAddressIsIncomplete = !shippingAddress;

    return (
      (isExpress && !isWithinThreshold) ||
      (isExpress && !isTimeEligibleForExpress(basket?.storeInfo?.[0].expressDeliveryTimeFrame as ExpressTimeFrame)) ||
      // isSelectedAddressIsIncomplete ||
      !hasProductInBasket ||
      !basket?.basketItems?.length ||
      (basketTotal < (basket?.minimumSpend || 0) && !(!has1PSelected && isFlexibleBasketEnabled)) ||
      noCurrentStore
    );
  };

  const handleCheckout = async (validatorFunction?: (basketItem: BasketLineItem[]) => boolean): Promise<void> => {
    setCheckoutInitiated(true);
    if (isExpress && !isTimeEligibleForExpress(basket?.storeInfo?.[0]?.expressDeliveryTimeFrame as ExpressTimeFrame)) {
      SendBrowserEvent.showErrorToast(undefined, t('express.message.outOfOrderTime'));
      setCheckoutInitiated(false);
      return;
    }
    try {
      if (validatorFunction) {
        dispatch(getBasketCalculationAction());

        if (!validatorFunction(basket?.basketItems || [])) {
          setCheckoutInitiated(false);
          return;
        }
      }

      if ((!zipcodeForUpdateBasket || !currentStore?.subdistrict) && isAddressFlowRevampEnabled) {
        throw new Error(BasketErrorTypes.MISSING_ZIP_OR_SUBDISTRICT);
      }

      const customerDefaultAddress = shippingAddress
        ? mapUserAddressToShippingAddress(shippingAddress, isThai, true)
        : undefined;

      const single1PItem = basket?.basketItems?.find(
        (item) =>
          item.productDetails?.variants[0]?.storeCode && is1PMakroStore(item.productDetails?.variants[0]?.storeCode)
      );

      if (
        isAddressFlowRevampEnabled &&
        single1PItem &&
        single1PItem.productDetails.variants[0].storeCode !== currentStore?.storeCodes[0]
      ) {
        await updateBasketStoreCode(currentStore?.zipcode || '', currentStore?.subdistrict || '', idToken || '');
        dispatch(getBasketCalculationAction());
        setCheckoutInitiated(false);
        return;
      }

      const basketItemPayload = basket?.basketItems
        .map((itm: any) => {
          if (persistedSelectedItems.includes(itm.productId)) {
            return {
              productId: itm.productId,
              quantity: itm.quantity
            };
          }
          return undefined; // Explicitly return undefined if the item isn't selected
        })
        .filter((item): item is BasketItemInput => Boolean(item));

      let checkoutId;
      let isFlexibleBasket = false;
      if (
        isFlexibleBasketEnabled &&
        persistedSelectedItems.length &&
        customerDefaultAddress &&
        basketItemPayload?.length
      ) {
        const checkoutPayload = createCheckoutPayload(
          'direct',
          undefined,
          customerDefaultAddress.zip as string,
          customerDefaultAddress?.subdistrict as string,
          customerDefaultAddress,
          i18n.language,
          true,
          true,
          basket?.basketItems?.length || 0,
          basket?.prices?.totalFinalPriceToPay || 0,
          accessTradeClickId,
          pricezaClickId,
          isExpress,
          appVersion,
          deviceOS,
          defaultMembershipCard?.id
        );

        const checkout = isCheckoutV3
          ? await createPartialCheckoutV3(checkoutPayload, basketItemPayload, idToken || '')
          : await createPartialCheckout(checkoutPayload, basketItemPayload, idToken || '');
        checkoutId = checkout?.id;
        isFlexibleBasket = true;
      } else {
        const checkout = await getCheckoutIdV2(
          idToken || '',
          zipcodeForUpdateBasket as string,
          currentStore?.subdistrict as string,
          customerDefaultAddress,
          i18n.language,
          true,
          true,
          basket?.basketItems?.length || 0,
          basket?.prices?.totalFinalPriceToPay || 0,
          accessTradeClickId,
          pricezaClickId,
          isExpress,
          appVersion,
          deviceOS,
          defaultMembershipCard?.id
        );
        checkoutId = checkout.checkoutId;
      }

      if (!checkoutId) {
        setCheckoutInitiated(false);
        return;
      }
      await addCheckoutStartAnalytics(checkoutId, isFlexibleBasket);
      SendBrowserEvent.goToCheckout(checkoutId);
      !isSyncBasket && localStorage.removeItem('selectedItems');
      if (deviceOS !== 'ios') {
        localStorage.removeItem('hasShownTooltip');
      }
      setCheckoutInitiated(false);
    } catch (error: any) {
      setCheckoutInitiated(false);
      sentry.captureException(error);
      if (error?.message?.includes('Express slots not available')) {
        SendBrowserEvent.showErrorToast(undefined, t('express.message.slotIsFull'));
      } else {
        SendBrowserEvent.showErrorToast(undefined, t('message.somethingWentWrong'));
      }
      console.error('An error occurred during checkout:', error);
    }
  };

  const addCheckoutStartAnalytics = async (checkoutId?: string, isFlexibleBasket?: boolean) => {
    await analytics.trackEvent(ANALYTICS.CHECKOUT_START, {
      itemsCount: isFlexibleBasket ? basket?.basketItemsFromFe?.length || 0 : basket?.basketItems?.length || 0,
      total: basket?.prices?.totalFinalPriceToPay || basket?.total || 0,
      voucher: basket?.loyaltyPoints ? '' : basket?.voucherId || '',
      loyaltyPoints: basket?.loyaltyPoints || 0,
      estimatedPoints: basket?.estimatedPointsReceive || 0,
      cart_id: basket?.id || '',
      basketId: basket?.id || '',
      checkout_id: checkoutId || '',
      checkoutId: checkoutId || '',
      delivery: basket?.prices?.shippingFee || 0,
      productIds: isFlexibleBasket
        ? basket?.basketItemsFromFe || []
        : basket?.basketItems?.map((item: any) => item.productId) || [],
      priceBeforeDiscount: basket?.prices?.totalOriginalPrice || '',
      discount: basket?.prices?.roundedDiscountAmount || '',
      voucherAmount: basket?.prices?.exactVoucherDiscountAmount || '',
      shippingAddress: (shippingAddress as any) || '',
      feType: 'SAC',
      channel: SUGGESTEDLIST_CHANNELS.MKP_WEB,
      fromExpress: isExpress,
      storeId: basket?.storeCode || '',
      segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      clickId: accessTradeClickId || '',
      clickIdSource: pricezaClickId || '',
      flexSize: persistedSelectedItems?.length
    });
  };

  const listItemsToBeAdjustedDueToInsufficientInventory = (basket: BasketState) => {
    const itemsToBeAdjusted: ItemToUpdateQuantity[] = [];
    if (!basket) return [];
    const basketItems = basket.basketItems || [];
    for (const item of basketItems) {
      const totalInventory = item.productDetails.totalInventory || 0;
      if (item.quantity > totalInventory && totalInventory > 0)
        itemsToBeAdjusted.push({
          variantId: item.variantId,
          quantity: item.productDetails.totalInventory
        });
    }
    return itemsToBeAdjusted;
  };

  const ensureItemsQuantityInBasketMatchWithInventory = async () => {
    if (!basket) return;
    const itemsToUpdate = listItemsToBeAdjustedDueToInsufficientInventory(basket);
    if (itemsToUpdate.length > 0) {
      const quantityChangedProductHash: IBasketQuantityChangedProductMap = {};
      itemsToUpdate.forEach((item) => {
        const product = basket.basketItems.find((product) => product.variantId === item.variantId);
        quantityChangedProductHash[item.variantId] = {
          currentQuantity: item.quantity,
          previousQuantity: product?.quantity
        };
      });
      dispatch(setBasketQuantityChangedProductMapAction(quantityChangedProductHash));
      dispatch(batchUpdateItemsQuantityAction(itemsToUpdate));
      SendBrowserEvent.showInfoToast(t('message.basketGotAffectedDueToAddressChange'));

      //set the flag of the items that were adjusted thier quantity to highllight to the user in the basket
      dispatch(setVariantIdsOfItemThatWasAdjustedQuantityAction(itemsToUpdate.map((item) => item.variantId)));
    } else {
      const variantIDs = Object.keys(basketQuantityChangedProductMap);
      const basketItems = basket.basketItems || [];
      const basketVariantIDs = basketItems.map((item) => item.variantId);
      const quantityChangedVariantIds = variantIDs.filter((id) => basketVariantIDs.includes(id));

      if (variantIDs.length > 0 && quantityChangedVariantIds.length === 0) {
        dispatch(setBasketQuantityChangedProductMapAction({}));
      }
    }
  };

  const renderAffectedBasketProduct = (itm: BasketLineItem, key: number, isUnavailable?: boolean) => {
    let originPrice = itm.productDetails.originPrice;
    let displayPrice = itm.productDetails.displayPrice;
    const [variant] = itm.productDetails.variants;
    originPrice = parseFloat(variant.compareAtPrice);
    if (itm.promotedPrice) {
      displayPrice = itm.promotedPrice;
    } else {
      displayPrice = variant.price;
    }

    const {status: productStatus, isAvailable} = getProductAvailability(itm.productDetails, currentStoreCodes || [], {
      alcoholExpiryDate: defaultMembershipCard?.alcoholExpiryDate
    });
    const isItemOnProhibitedPeriod =
      itm.productDetails.unavailableReason?.code === ProductUnavailableReasonCodes.ALCOHOL_PROHIBITED_PERIOD;

    return (
      <StyledProductInfoWrapper key={itm.id} isUnavailable={!!isUnavailable}>
        <ProductItem
          isProductUnavailable={!isAvailable || isItemOnProhibitedPeriod}
          productUnavailableText={isItemOnProhibitedPeriod ? t(`label.restricted`) : t(`label.${productStatus}`)}
          title={itm.productDetails.title}
          coverImageURL={itm.productDetails.imageUrls[0]}
          originPrice={originPrice}
          displayPrice={displayPrice}
          priceUnit={itm.productDetails.priceUnit}
          quantity={itm.quantity}
          weight={itm.productDetails?.size || ''}
          productURL={`/p/${itm.productId}`}
          maxQuantity={itm.productDetails.totalInventory}
          promotedPrice={itm.productDetails.promotedPrice}
          basketItem={itm}
        />
        {basket && basket?.basketItems?.length - 1 === key ? null : <Divider />}
      </StyledProductInfoWrapper>
    );
  };

  const renderBasketAffectedModalBody = (): JSX.Element => {
    const productChanges: BasketLineItem[] = unavailableProducts;

    return (
      <Box mt={1}>
        <When condition={unavailableProducts.length}>
          <Typography fontSize={16} fontWeight={700} ml={-0.5}>
            {t('label.remove')}
          </Typography>
          <StyledSectionWrap className={`${productChanges.length > 1 ? 'multiple' : 'single'}`}>
            <StyledSectionContentContainer>
              {unavailableProducts.map((itm, key) => {
                return renderAffectedBasketProduct(itm, key, true);
              })}
            </StyledSectionContentContainer>
          </StyledSectionWrap>
        </When>
      </Box>
    );
  };

  const processPostCheckoutClick = () => {
    const validateCheckout = (newlyFetchedItems: BasketLineItem[]) => {
      const newUnavailableItems = filterUnavailableProducts(newlyFetchedItems, currentStoreCodes || []);
      if (newUnavailableItems.length > 0) {
        SendBrowserEvent.showErrorToast(undefined, t('message.productsAreOutOfStock'));
        return false;
      }
      return true;
    };
    return handleCheckout(validateCheckout);
  };

  const newOnCheckoutClick = async (isOOSProductsDeleted = false) => {
    const hasUnavailableProducts = unavailableProducts.length > 0;
    if (!isOOSProductsDeleted) {
      const isModalShown = hasUnavailableProducts;
      if (isModalShown) {
        setBasketAffectedModalData({
          ...basketAffectedModalData,
          modalShown: isModalShown,
          notReadyToPurchase: unavailableProducts.length,
          quantityUpdatedItems: 0
        });
        return;
      }
    } else {
      setBasketAffectedModalData({
        ...basketAffectedModalData,
        modalShown: false
      });
    }

    processPostCheckoutClick();
  };

  const handleConfirmAndContinue = async () => {
    try {
      dispatch(removeUnavailableItemsAction(newOnCheckoutClick));
    } catch (error: any) {
      SendBrowserEvent.showErrorToast(undefined, error.message || t('global.message.somethingWentWrong'));
    } finally {
      setBasketAffectedModalData({
        modalShown: false,
        notReadyToPurchase: 0,
        quantityUpdatedItems: 0
      });
    }
  };

  const editAddress = (address: UserAddress) => {
    setAddressBeingEdited(address);

    const initialLat = address.user_latitude || BangkokCoordinates.lat;
    const initialLng = address.user_longitude || BangkokCoordinates.lng;

    const newLat = address.locus_latitude ? address.locus_latitude : initialLat;
    const newLng = address.locus_longitude ? address.locus_longitude : initialLng;

    setPinMapData({
      ...pinMapData,
      coordinates: {
        lat: newLat,
        lng: newLng
      },
      zoomLevel: Number(address.metadata?.zoomLevel) || 18,
      address: address.address1,
      postalCode: address.zip,
      district: isThai ? address.district?.nameTh : address.district?.nameEn,
      province: isThai ? address.province?.nameTh : address.province?.nameEn,
      subdistrict: isThai ? address.subDistrict?.nameTh : address.subDistrict?.nameEn
    });

    if (isPinMapV2Enabled) {
      _showPinMapModal();
    } else {
      setIsAddressFormModalOpen(true);
    }
  };

  const onCheckoutClick = async () => {
    if (!shippingAddress?.id) {
      if (userAddressList?.length) {
        toggleAddressDrawer();
        SendBrowserEvent.showInfoToastEnabled(t('message.selectShippingAddress'));
        return;
      } else {
        _onNewAddress();
        SendBrowserEvent.showInfoToastEnabled(t('message.completeShippingAddress'));
        return;
      }
    } else if (!shippingAddress?.user_latitude || !shippingAddress?.user_longitude) {
      setAddressBeingEdited(shippingAddress);
      setIsAddressFormModalOpen(true);
      if (timer.current) {
        clearTimeout(timer.current);
      }
      timer.current = setTimeout(() => {
        setOpenFullMapView(true);
      }, 1000);
      return;
    }
    if (checkIfAddressLocationIsDeliverable(locations, shippingAddress)) {
      SendBrowserEvent.showErrorToast(undefined, t('message.undeliverableAddressLocation'));
      return;
    }
    if (getAlcoholProducts(basket?.basketItems as BasketLineItem[]).length) {
      const selectedAlcoholBasketItems = basket?.basketItems?.filter(
        (item) => persistedSelectedItems.includes(item.productId) && item.productDetails.isAlcohol
      ) as BasketLineItem[];
      const isSufficientAlcoVolume = getAlcoholVolumeInLiters(selectedAlcoholBasketItems) >= ALCOHOL_VOLUME_THRESHOLD;
      if (!isSufficientAlcoVolume && selectedAlcoholBasketItems.length) {
        SendBrowserEvent.showErrorToast(undefined, t('alcohol.message.addMoreSelectedAlcohol'));
        return;
      }
    }
    newOnCheckoutClick(isFlexibleBasketEnabled);
  };

  useEffect(() => {
    if (userAddressList.length && currentStore) {
      dispatch(updateBasketAddressAction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userAddressList, currentStore]);

  useEffect(() => {
    if (currentStore?.zipcode && currentStore?.subdistrict && !isAppLoading && idToken) {
      dispatch(getBasketCalculationAction({isEmptyBasketPayload: true}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultMembershipCard, isAppLoading]);

  useEffect(() => {
    if (currentStore && !isAppLoading && isBasketRecommendationEnabled() && idToken && customerId) {
      dispatch(getBasketRecommendationsAction());
    }
  }, [defaultMembershipCard, isAppLoading, currentStore, dispatch, idToken, customerId]);

  useEffect(() => {
    if (idToken && config.IS_ADDRESS_SECTIONS_ENABLED) {
      dispatch(fetchUserDetailsAction());
    }
  }, [dispatch, idToken]);

  useEffect(() => {
    if (isDirectCheckout) toggleAddressDrawer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirectCheckout]);

  useEffect(() => {
    ensureItemsQuantityInBasketMatchWithInventory();
    if (basket?.basketItems.length && !persistedSelectedItems && isFlexibleBasketEnabled) {
      const listOfProductIds = getIdsFromBasketItems(basket?.basketItems || []);
      dispatch(setSelectedItemsAction(listOfProductIds || []));
      !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify(listOfProductIds || []));
    } else if (
      basket?.basketItems.length &&
      persistedSelectedItems?.length &&
      isFlexibleBasketEnabled &&
      !selectedItems?.length
    ) {
      dispatch(setSelectedItemsAction(persistedSelectedItems || []));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basket]);

  useEffect(() => {
    if (!isWithinThreshold && isExpress && threshold) {
      SendBrowserEvent.showErrorToast(undefined, t('express.message.basketIsFull', {threshold}));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWithinThreshold, isExpress, threshold]);

  const isBasketLoading =
    (!(basket && Object.keys(basket).length > 0) && !isBasketApiFailed && !!idToken) || isLoading || isAppLoading;

  const updateStoresForBasket = async (address: UserAddress) => {
    const storeInfo = await MultiStoreService.getStoreInfoByLocation(
      idToken,
      address.zip,
      address.subDistrict?.nameEn || ''
    );
    if (isSyncAddress) {
      dispatch(updateMetaDataAction({address, storeInfo}));
      return;
    }
    const basketStore = await updateBasketStoreCode(address.zip, address?.subDistrict?.nameEn || '', idToken);
    if (basketStore?.updateBasketStoreCode?.status === 'SUCCESS') {
      const showStorePickup =
        storeInfo?.stores?.[0].warehouseStoreData?.storeConfigurations?.pickupAtStoreEnable || false;
      const storeCodes = storeInfo?.stores?.map((store) => store.storeCode) ?? [];
      const currentStorePayload = {
        zipcode: storeInfo?.zipcode,
        subdistrict: storeInfo.subdistrict,
        storeCodes,
        showStorePickup,
        isSameDayDelivery: storeInfo?.stores[0]?.sameDay
      };
      dispatch(setCurrentStoreAction(currentStorePayload));
      dispatch(getBasketCalculationAction());
    }
  };

  const onClickSelectAllItems = (e: any) => {
    if (e.target.checked) {
      const allItemsIds = basket?.basketItems.map((item) => item.productId);
      if (allItemsIds?.length) {
        dispatch(setSelectedItemsAction(allItemsIds));
        !isSyncBasket && localStorage.setItem('selectedItems', JSON.stringify(allItemsIds));
        dispatch(getBasketCalculationAction());
      }
    } else {
      dispatch(setSelectedItemsAction([]));
      isSyncBasket ? dispatch(getBasketCalculationAction()) : localStorage.setItem('selectedItems', JSON.stringify([]));
    }
  };

  const _onPinMapSelect = (newPinMapData: TPinMapSelectedLocation) => {
    if (newPinMapData?.coordinates) {
      setPinMapData(newPinMapData);

      setIsAddressFormModalOpen(true);
    } else {
      setIsPinMapViewOpen(false);
      //close address list as well if no address is selected
      // if (!addressBeingEdited?.id) {
      //   // if new address, redirect back to basket view
      //   setAddressDrawerOpen(false);
      // }
    }
  };

  const _onSaveAddress = (open: boolean, isSaved = false) => {
    setIsAddressFormModalOpen(open);

    if (isSaved) {
      setIsPinMapViewOpen(false);
    }
  };

  const addAddressEvent = (eventName: string, payload: any) => {
    analytics.trackEvent(eventName, {
      zip: currentStore?.zipcode,
      sub: currentStore?.subdistrict || '',
      addressesTotal: userAddressList?.length,
      storeId: basket?.storeCode || '',
      channel: SUGGESTEDLIST_CHANNELS.MAKRO_PRO,
      segment_source_name: isReactNativeWebView ? EVENT_SOURCE.APP : EVENT_SOURCE.WEB,
      basketSize: basket?.basketItems?.length || 0,
      onePCount: filter1PItems(basket?.basketItems || []).length || 0,
      threePCount: filter3PItems(basket?.basketItems || []).length || 0,
      ...payload
    });
  };

  const _showPinMapModal = () => {
    setIsPinMapViewOpen(true);
    addAddressEvent(ANALYTICS.ADDRESS_LOCATION_PROMPT, {});
  };

  const _onNewAddress = () => {
    setPinMapData(null);
    setAddressBeingEdited(undefined);
    if (isPinMapV2Enabled) {
      _showPinMapModal();
    } else {
      setIsAddressFormModalOpen(true);
    }
  };

  return (
    <StyledBasket open={true} transitionDuration={0} variant="persistent">
      <Header
        address={shippingAddress as UserAddress}
        isLoadingAddress={checkoutInitiated || isBasketLoading}
        onShipToClick={() => onShipToClickHandle()}
        title={isExpress ? t('express.title.expressBasket') : t('title')}
        isFlexibleBasketEnabled={isFlexibleBasketEnabled}
        setShowConfirmModal={setShowConfirmModal}
      />
      <Body
        loading={isBasketLoading}
        address={shippingAddress as UserAddress}
        isLoading={checkoutInitiated}
        onClick={() => onShipToClickHandle()}
      />
      <Footer
        shouldShowMinBasketWarning={has1PSelected}
        onCheckoutClick={onCheckoutClick}
        isCheckoutDisabled={isDisabledCheckoutBtn()}
        loading={checkoutInitiated || isBasketLoading}
        onClickSelectAllItems={onClickSelectAllItems}
        setShowBasketSummaryModal={setShowBasketSummaryModal}
        showBasketSummaryModal={showBasketSummaryModal}
      />
      <AddressDrawer
        title={t('label.shippingAddress')}
        open={isAddressDrawerOpen}
        toggleAddressDrawer={toggleAddressDrawer}
        onEditAddress={editAddress}
        onNewAddress={_onNewAddress}
        shippingAddress={shippingAddress}
        setOpenFullMapView={setOpenFullMapView}
        updateStoresForBasket={updateStoresForBasket}
      />
      {isAddressFormModalOpen ? (
        <AddressFormView
          modalOpen={isAddressFormModalOpen}
          setModalOpen={_onSaveAddress}
          address={addressBeingEdited}
          initialPinMapData={pinMapData}
          onFormSubmit={() => {
            //? don't close address drawer if address is being edited
            if (!addressBeingEdited?.id) {
              setAddressDrawerOpen(false);
            }
          }}
          openFullMapView={openFullMapView}
          setOpenFullMapView={setOpenFullMapView}
          updateStoresForBasket={updateStoresForBasket}
        />
      ) : null}
      {isPinMapViewOpen ? (
        <PinMapModal
          initialPinMapData={pinMapData}
          open={isPinMapViewOpen}
          isAddressFormOpen={isAddressFormModalOpen}
          onSave={_onPinMapSelect}
          addAddressEvent={addAddressEvent}
        />
      ) : null}

      <BasketPopUpDialog
        open={basketAffectedModalData.modalShown}
        title={t('label.basketUpdate')}
        modalInfo={t('message.notAvailableProductsWillBeRemoved')}
        confrmButtonText={t('button.confirm&continue')}
        onGoBack={() => {
          setBasketAffectedModalData({
            ...basketAffectedModalData,
            modalShown: false
          });
        }}
        loading={checkoutInitiated}
        onConfirmAndContinue={handleConfirmAndContinue}
      >
        {renderBasketAffectedModalBody()}
      </BasketPopUpDialog>

      <FlexiBasketConfirmModal
        onConfirm={() => {
          dispatch(deleteSelectedItemsAction());
          setShowConfirmModal(false);
        }}
        setShowConfirmModal={setShowConfirmModal}
        open={showConfirmModal}
        message={t('flexiBasket.deleteModalInfoText', {
          numberOfItems
        })}
        isReactNativeWebView={isReactNativeWebView}
      />
      <BasketSummaryModal
        showBasketSummaryModal={showBasketSummaryModal}
        setShowBasketSummaryModal={setShowBasketSummaryModal}
        onCheckoutClick={onCheckoutClick}
        isCheckoutDisabled={isDisabledCheckoutBtn()}
        loading={checkoutInitiated || isBasketLoading}
        onClickSelectAllItems={onClickSelectAllItems}
      />
    </StyledBasket>
  );
};

export default BasketWrapper;
